import microuniversoApi from "app/api/microuniversoApi";
import { useAppSelector } from "app/redux/hooks";
import { Documento, RespostaDocumentosAbertos } from "app/types/cliente/RespostaDocumentosAbertos";
import { tratamentoErroPadrao } from "app/use-cases";
import { Dict } from "app/utils/Dict";
import globalMU from "globalMU";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Breadcrumb2Container } from "../breadcrumb2/Breadcrumb2Container";
import { DocumentosAbertosInformacoesContainer } from "../documentos-abertos-informacoes/DocumentosAbertosInformacoesContainer";
import { renderizarComponentReact } from "../renderizarOuRemoverComponentReact";
import { DocumentosAbertos } from "./DocumentosAbertos";
import { obterParametros, obterParametrosPorOidItem } from "app/use-cases/parametro/obterParametros";

const parametroFonteReceita = "UTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA";

export type DocumentosAbertosContainerProps = {
    codigoCliente: number
    onVoltar: () => void;
    passos: Dict<string, () => void>;
    onRemover: () => boolean
};

export const DocumentosAbertosContainer = (props: DocumentosAbertosContainerProps) => {
    const { codigoCliente, onRemover, onVoltar: onVoltarProp, passos } = props;
    const orcamento = useAppSelector((store) => store.orcamento);
    const [dados, setDados] = useState<Partial<RespostaDocumentosAbertos>>();
    const [colunaOrdenacao, setColunaOrdenacao] = useState<string>("")
    const [ordemOrdenacaoDecrescente, setOrdemOrdenacaoDecrescente] = useState<boolean>(false)
    const [paramUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA, setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA] = useState<boolean>(false)
    const isMounted = useRef(true)
    const passosInterno = useRef<Dict<string, () => void>>(new Dict(Object.assign({}, passos.map)))

    const propsRenderizacao = useMemo(() => ({
        codigoCliente,
        onVoltar: onVoltarProp,
        passos,
        onRemover
    }), [codigoCliente, onVoltarProp, passos, onRemover])

    const onVoltar = useCallback(() => {
        onRemover()
        onVoltarProp()
    }, [onRemover, onVoltarProp])

    const atualizarParametros = useCallback(async () => {
        let oidFilial = orcamento?.OidFilialOrcamento ?? 7;

        try {
            if (oidFilial > 7) {
                const param = await obterParametrosPorOidItem(
                    {
                        nomes:
                            [
                                parametroFonteReceita,
                            ], oidItem: oidFilial
                    })

                if (!isMounted.current) return;
                setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA(param.find((p) => p.Nome == parametroFonteReceita)?.Valor == "S") // 487678/449093 
            }
            else {
                // se chamado fora do orçamento
                const param = await obterParametros({
                    nomes: [
                        parametroFonteReceita,
                    ],
                })

                if (!isMounted.current) return;
                setParamUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA(param.find((p) => p.Nome == parametroFonteReceita)?.Valor == "S")  // 487678/449093 
            }
        }
        catch (e) {
            if (isMounted.current) tratamentoErroPadrao(e)
        }
    }, [orcamento?.OidFilialOrcamento])

    const dadosDocumentosAbertos = useCallback(async () => {
        try {
            globalMU.blockUI();
            const resultado = await microuniversoApi.Cliente.DadosDocumentoAberto(
                {
                    CodigoCliente: codigoCliente,
                    NumeroPagina: 1,
                    TamanhoPagina: 20,
                    TipoConsulta: "EA"
                },
            );

            if (!isMounted.current) return;
            (!("HasErrors" in resultado)) ? setDados(resultado) : (setDados({}), onVoltar());

        }
        catch (e) {
            if (isMounted.current) tratamentoErroPadrao(e)
        }
        finally {
            globalMU.unblockUI();
        }
    }, [codigoCliente, onVoltar])

    const listarDcouemntosAbertos = useCallback(async (coluna?: string, decrescente?: boolean) => {
        try {
            globalMU.blockUI();
            const resultado = await microuniversoApi.Cliente.DadosDocumentoAberto(
                {
                    CodigoCliente: codigoCliente,
                    NumeroPagina: 1,
                    TamanhoPagina: 20,
                    TipoConsulta: "EA"
                },
                coluna,
                decrescente
            );

            if (!isMounted.current) return;
            setColunaOrdenacao(coluna ?? "")
            setOrdemOrdenacaoDecrescente(decrescente ?? false)
            setDados(resultado)

        }
        catch (e) {
            if (isMounted.current) tratamentoErroPadrao(e)
        }
        finally {
            globalMU.unblockUI();
        }
    }, [codigoCliente])

    const detalharDocumento = useCallback((documento: Documento) => {
        renderizarComponentReact(<DocumentosAbertosInformacoesContainer documento={documento}
            onRemover={onRemover}
            onVoltar={() => renderizarComponentReact(<DocumentosAbertosContainer {...propsRenderizacao} />, "conteudo-pagina", true)}
            passos={passosInterno.current} />, "conteudo-pagina", true)
    }, [onRemover, propsRenderizacao])

    const onPassoSelecionado = useCallback((passo: string) => {
        onRemover()
        passosInterno.current.get(passo)()
    }, [onRemover])

    useEffect(() => {
        isMounted.current = true

        return () => {
            isMounted.current = false
        }
    }, [])

    useEffect(() => {
        atualizarParametros() // 487678/449093 
        passosInterno.current.set("Documentos Abertos", () => { renderizarComponentReact(<DocumentosAbertosContainer {...propsRenderizacao} />, "conteudo-pagina", true) })
        dadosDocumentosAbertos()
    }, [atualizarParametros, dadosDocumentosAbertos, propsRenderizacao])

    return (
        <div style={{ overflow: "auto" }}>
            <Breadcrumb2Container
                passos={passosInterno.current.keysToList()}
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                onBotaoVoltarClicado={onVoltar}
                onPassoSelecionado={onPassoSelecionado}
                id="DocumentosAbertosBreadcrumb"
            />

            <DocumentosAbertos
                dadosDocumentosAbertos={dados}
                cpfcnpj={orcamento.Cliente.CpfCnpj}
                nome={orcamento.Cliente.Nome}
                onInfo={detalharDocumento}
                colunaOrdenacao={colunaOrdenacao}
                ordemOrdenacaoDecrescente={ordemOrdenacaoDecrescente}
                onAlterarOrdenacaoDados={listarDcouemntosAbertos}
                usaFonteDeReceita={paramUTILIZA_RELACIONAMENTO_FILIAL_VENDEDOR_FONTE_RECEITA} // 487678/449093 
            />
        </div>

    )
}
